import {useLocationState} from '@raison/navigation';
import bankIcon from '@raison/static/icons/bank-line.svg';
import {
    InlineSvg,
    RootLayout,
    RsnAmount,
    RsnButton,
    RsnCopyButton,
    RsnDialog,
    RsnMessage,
    RsnSelect,
    RsnSkeleton,
} from '@raison/ui-kit';
import {useQuery} from '@tanstack/react-query';
import * as Schema from 'effect/Schema';
import React, {useState} from 'react';
import {useNavigate} from 'react-router';
import {useCurrencyFormat} from '~/entities/currencies';
import {OperationDetail, OperationInfo} from '~/entities/operations-fiat';
import {LabelWithInfo} from '~/processes/fiat-operations/ui/LabelWithInfo';
import {OperableAccount, OperableBank} from '~/redux/finances/domain';
import {useFinishTopupBank} from '../../api/topup/finishTopupBank';
import {useTopupFeeOptions} from '../../api/topup/topupFee';
import {useTopupRequisites} from '../../api/topup/topupRequisites';
import {TotalReceiveDialog} from '../Shared/TotalReceiveDialog';

export type TopupRequisitesProps = {
    amount: RsnAmount;
    account: OperableAccount;
    method: OperableBank;
};

export function TopupRequisites({account, method, amount}: TopupRequisitesProps) {
    const navigate = useNavigate();

    const {data: {value: fee} = {}, isLoading: isFeesLoading} = useQuery(
        useTopupFeeOptions({
            accountId: account.id,
            methodType: method?.type,
            methodId: method?.id,
            amount: RsnAmount.toString(amount) || '0',
        })
    );

    const {data: banks = [], isLoading: isRequisitesLoading} = useTopupRequisites({
        accountId: account.id,
        methodType: method.type,
        methodId: method.id,
    });
    const {format: formatAsSubject} = useCurrencyFormat(account);

    const banksOptions: RsnSelect.Option<number>[] = banks.map(bank => ({
        value: bank.id,
        label: bank.name,
        icon: {
            element: (
                <div className={'flex size-9 items-center justify-center rounded-full bg-neutral-20'}>
                    <InlineSvg src={bankIcon} className={'size-5 text-neutral-90'} />
                </div>
            ),
        },
    }));

    const firstBankId = banksOptions?.[0]?.value;
    const [selectedBankId, setSelectedBankId] = useState(firstBankId);

    const currentBankId = selectedBankId ?? firstBankId;
    const currentBank = banks?.find(b => b.id === currentBankId);

    const t = {
        title: (() => {
            switch (method.method) {
                case 'sepa': {
                    return 'EU bank top up';
                }
                case 'swift': {
                    return 'Worldwide top up';
                }
                default: {
                    return 'Top up';
                }
            }
        })(),
        warning: ' Payments must be sent from your named account otherwise it would be rejected.',
        bankSelectLabel: 'Bank for receiving',
        feeLabel: 'Fee',
        amountLabel: 'Total amount',
        totalLabel: (() => {
            if (fee == null) {
                return 'Total';
            }
            return fee.commissionAbove ? 'Total spend' : 'Total receive';
        })(),
        total: formatAsSubject(fee?.total ?? 0),
        button: 'Done',
    };

    const {isPending, mutateAsync} = useFinishTopupBank(account.id);

    const handleDone = async () => {
        const {ok} = await mutateAsync({
            bank_id: currentBankId,
            amount: RsnAmount.toString(amount),
            payment_network: Number(method.id),
        });
        if (ok) {
            navigate('/assets');
        }
    };

    const [dialog, setDialog] = useLocationState('topup-requisited-dialog', Schema.Literal('total'));
    const closeDialog = () => navigate(-1);

    return (
        <RootLayout common title={t.title}>
            <RsnMessage.FootnoteV2>{t.warning}</RsnMessage.FootnoteV2>
            <RsnSelect
                label={t.bankSelectLabel}
                options={banksOptions}
                value={currentBankId}
                onChange={setSelectedBankId}
                disabled={!isRequisitesLoading && banksOptions.length < 2}
            />
            {isRequisitesLoading ? (
                <div className={'relative flex flex-col gap-4'}>
                    {Array.from({length: 10}).map((_, index) => (
                        <div key={index} className='flex flex-col gap-1'>
                            <RsnSkeleton width={80} height={16} />
                            <RsnSkeleton width={200} height={20} />
                        </div>
                    ))}
                    <div className={'absolute z-10 h-full w-full bg-gradient-to-b from-transparent to-neutral-10'} />
                </div>
            ) : (
                <div className={'flex flex-col gap-4'}>
                    {currentBank?.requisite?.map(({key, label, value}) => (
                        <OperationInfo key={key} label={label}>
                            <div className={'flex items-center justify-between gap-6'}>
                                <div>{value}</div>
                                <div className={'flex shrink-0'}>
                                    <RsnCopyButton size={20} value={value} />
                                </div>
                            </div>
                        </OperationInfo>
                    ))}
                </div>
            )}
            <hr className={'mt-auto border-b border-solid border-neutral-20'} />
            <div className={'flex flex-col gap-4'}>
                <OperationDetail.Layout>
                    {isFeesLoading ? (
                        <>
                            <OperationDetail label={t.feeLabel}>
                                <RsnSkeleton width={60} height={16} />
                            </OperationDetail>
                            <OperationDetail label={t.amountLabel}>
                                <RsnSkeleton width={60} height={16} />
                            </OperationDetail>
                            <OperationDetail label={t.totalLabel}>
                                <RsnSkeleton width={60} height={16} />
                            </OperationDetail>
                        </>
                    ) : (
                        <>
                            <OperationDetail label={t.feeLabel}>{formatAsSubject(fee?.fee ?? 0)}</OperationDetail>
                            <OperationDetail label={t.amountLabel}>
                                {formatAsSubject(fee?.inputAmount ?? 0)}
                            </OperationDetail>
                            <OperationDetail.Total
                                label={<LabelWithInfo onClick={() => setDialog('total')}>{t.totalLabel}</LabelWithInfo>}
                            >
                                {formatAsSubject(fee?.total ?? 0)}
                            </OperationDetail.Total>
                        </>
                    )}
                </OperationDetail.Layout>
                <RsnButton.Primary disabled={isPending} onClick={handleDone}>
                    {t.button}
                </RsnButton.Primary>
            </div>

            <RsnDialog.Controller onClose={closeDialog}>
                {dialog === 'total' && <TotalReceiveDialog />}
            </RsnDialog.Controller>
        </RootLayout>
    );
}
